import { SERIES_METRICS } from './metrics-series.js';

/**
 * The three numbers the product is sold on — lies detected, false alarms, correct videos — marked
 * as the platform's source of truth wherever a screen shows them.
 *
 * ## Why primary
 *
 * One hue for the three metrics, because it says «source of truth», not «which metric». It is the
 * platform's own primary (product owner, 2026-10-29: «el color principal de la plataforma»), which
 * replaced the former chart hue — a magenta that read as a fourth verdict beside success and
 * danger. It is reached only through `primary` utilities, never through `brand-*` or the
 * Heartbeat classes.
 *
 * ## Where the identity lives, and where it never goes
 *
 * On the metric's name, a top edge, a glyph and the weight. Never on a digit, a verdict dot or a
 * verdict wash: those already say whether the number meets its target, and a magenta digit would
 * turn the identity into a fourth verdict.
 *
 * - **Top edges, not left rails.** A left rail already means status in `GroupCard` and `RunRow`.
 *   The only left mark is the GroupsCompare sticky row spine, where no status rail exists.
 * - **The band stays behind ink.** Muted text on it falls to 4.40:1, so it is never laid behind
 *   muted text, «sin datos» or `ArDeltaChip`; secondary text on it is `onBand`.
 * - **tailwind-merge.** Never pass a `text-*` colour through `class` into an `Ar*` component
 *   whose base carries `text-ui-*` (`ArDeltaChip`, `ArMeter`): the merge reads both as the same
 *   group and drops the size. `ArTableHead` and `ArTableCell` are safe.
 *
 * ## Declared deviation
 *
 * `ArKpiCard`'s doctrine is that text is always ink. Here the labels are coloured; the digits
 * never are. Design system requests that would retire this file's workarounds: a semantic token
 * aliasing primary for the metrics (such as `--c-key-metric`), `tone` and `caption` on `ArKpiCard`, a categorical
 * chip, and an accent-frame primitive.
 */

/** Pinned by the spec against SERIES_METRICS: a fourth metric fails the spec until it is given a glyph. */
export type ProductMetricKey = 'incoherences_detected_pct' | 'false_positives_pct' | 'correct_pct';

export const PRODUCT_METRIC_KEYS: readonly string[] = Object.freeze(
  SERIES_METRICS.map((metric) => metric.key),
);

const PRODUCT_METRIC_SET: ReadonlySet<string> = new Set(PRODUCT_METRIC_KEYS);

export function isProductMetric(key: string | null | undefined): key is ProductMetricKey {
  return typeof key === 'string' && PRODUCT_METRIC_SET.has(key);
}

const PERCENT = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 1 });

/**
 * A product-metric figure as every screen writes it: «72 %», «1,8 %»; «—» for none.
 *
 * One formatter for all of them, because these numbers are the product's source of truth and the
 * same figure read «72.4 %» on one screen and «72,4 %» on the next. The space before the sign is
 * non-breaking: at the tiles' 30 px a plain one let «%» drop onto a line of its own.
 */
export function formatPct(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';

  return `${PERCENT.format(value)}\u00a0%`;
}

/**
 * Whether a metric's name overflows its room, so only its glyph should show.
 *
 * More than a pixel, because sub-pixel rounding makes the two widths disagree by a fraction on a
 * label that fits, and a name folding away over a rounding error would flicker.
 */
export function labelOverflows(scrollWidth: number, clientWidth: number): boolean {
  return scrollWidth - clientWidth > 1;
}

/**
 * Whether the room grew since the name was folded away, and so is worth trying again.
 *
 * Only growth: with the name folded, a shrinking room cannot make it fit, and retrying on every
 * change would show the cut-off name for a frame each time.
 */
export function roomGrew(width: number, previous: number): boolean {
  return width > previous + 0.5;
}

/**
 * Every class the identity uses, as whole literals so Tailwind finds them.
 *
 * The `replaces` notes are load-bearing: these are swapped in for the plain classes, never added
 * on top of them.
 */
export const PRODUCT_METRIC_CLASS = Object.freeze({
  /** A metric's name. Size and case come from the context. */
  label: 'font-semibold text-primary',
  glyph: 'size-3.5 shrink-0 text-primary',
  glyphChip:
    'inline-flex size-5 shrink-0 items-center justify-center rounded-sm bg-primary/10 text-primary',
  /** A group of figures: the top line sits above it. */
  lid: 'relative pt-2',
  lidEdge: 'pointer-events-none absolute inset-x-0 top-0 h-0.5 rounded-pill bg-primary',
  /** Replaces `shadow-card`, never stacked with it. */
  tileFrame: 'relative overflow-hidden shadow-card-2',
  tileEdge: 'pointer-events-none absolute inset-x-0 top-0 h-1 bg-primary',
  tileLabel: 'text-ui-tag uppercase tracking-wide',
  /**
   * Replaces `text-2xl font-semibold`: a green verdict digit stays large text at 24 px bold — a
   * step under the 30 px it was (product owner, 2026-10-28: the metric cards took too much room).
   */
  tileValue: 'text-2xl font-bold',
  /** Replaces `text-ui-kpi font-semibold`. */
  trioValue: 'text-ui-kpi font-bold',
  /** The glyph beside a tile's figure, sized to its digits. */
  tileGlyph: 'size-5 shrink-0 text-primary',
  /** The glyph beside a card's figure. */
  trioGlyph: 'size-5 shrink-0 text-primary',
  /**
   * A figure on a control group's card («Control», product owner 2026-10-14; compact since
   * 2026-10-28): 20 px, so three fit a card's row without crowding it. Its name is the metric
   * card's eyebrow and wraps rather than folds (`ProductMetricFigure`).
   */
  heroValue: 'text-xl font-bold tracking-tight',
  heroGlyph: 'size-4 shrink-0 text-primary',
  /** Table and compare digits: `font-medium` renders as 400 with this family. */
  figure: 'font-semibold',
  /** Replaces `text-p-accent`: the sparkline draws with `currentColor`. */
  sparkline: 'text-primary',
  /** Table-head cap. */
  head: 'shadow-[inset_0_2px_0_var(--primary)]',
  /** Only behind ink or ink-2 text. */
  band: 'bg-primary/5',
  /** Secondary text on the band: muted falls to 4.40:1 there. */
  onBand: 'text-ink-2',
  /** GroupsCompare sticky metric rows only. */
  rowSpine: 'shadow-[inset_3px_0_0_var(--primary)]',
});
